// The report and contact form.
//
// One form for both, because "the bus times are wrong" and "can I ask you
// something" are the same act from the reader's side — they have something to
// tell us and want somewhere to put it. Splitting them into two buttons would
// make people choose a category before they have written a sentence, and the
// usual result of that is they choose neither.
//
// The button stays hidden until the backend says reports are configured. A
// contact form that silently drops what it is given is worse than not having
// one, and this is the only honest way to have the markup ship regardless.

import { BASE } from './http.ts';

/**
 * What the map was showing, attached to bug reports.
 *
 * "The map is blank" is unactionable; "the map is blank at zoom 14 over
 * Torry in Firefox on Android" is a bug someone can look for. All of it is
 * either already in the request headers or visible on the reader's own screen,
 * so none of it tells us anything they have not already shown us.
 */
export function reportContext(map?: { centre?: () => { lat: number; lon: number; zoom: number } }):
Record<string, string> {
  const context: Record<string, string> = {
    page: location.pathname + location.search,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    // Not the full string: it is long, and the parts that identify a rendering
    // problem are the engine and the platform.
    browser: navigator.userAgent.slice(0, 200),
    at: new Date().toISOString(),
  };

  try {
    const view = map?.centre?.();
    if (view) {
      context.view = `${view.lat.toFixed(4)}, ${view.lon.toFixed(4)} @ z${view.zoom.toFixed(1)}`;
    }
  } catch {
    // A missing view is not worth failing a bug report over.
  }
  return context;
}

interface ReportMap {
  centre?: () => { lat: number; lon: number; zoom: number };
}

export function wireReport(map?: ReportMap): void {
  const button = document.getElementById('report-link') as HTMLButtonElement | null;
  const dialog = document.getElementById('report') as HTMLDialogElement | null;
  const form = document.getElementById('report-form') as HTMLFormElement | null;
  const result = document.getElementById('rp-result');
  const send = document.getElementById('rp-send') as HTMLButtonElement | null;
  if (!button || !dialog || !form || !result || !send) return;

  // Asked once, on idle rather than on load: whether a button appears is not
  // worth a request that competes with the map for the first second.
  const ask = () => {
    void fetch(`${BASE}/api/v1/contact`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((d: { enabled?: boolean }) => { button.hidden = !d.enabled; })
      .catch(() => { /* leave it hidden */ });
  };
  const idle = (window as unknown as {
    requestIdleCallback?: (cb: () => void) => void;
  }).requestIdleCallback;
  if (idle) idle(ask); else window.setTimeout(ask, 2_000);

  const title = document.getElementById('report-title')!;
  const setKind = () => {
    const kind = new FormData(form).get('kind');
    title.textContent = kind === 'bug' ? 'Report a problem' : 'Get in touch';
  };
  form.addEventListener('change', setKind);

  button.addEventListener('click', () => {
    result.textContent = '';
    result.className = 'rp-result';
    setKind();
    dialog.showModal();
  });

  document.getElementById('rp-close')?.addEventListener('click', () => dialog.close());

  form.addEventListener('submit', async (event) => {
    // The form is method="dialog", which would close it on submit — including
    // on a failure the reader needs to see and retry.
    event.preventDefault();

    const data = new FormData(form);
    const kind = String(data.get('kind') || 'bug');
    const message = String(data.get('message') || '').trim();
    if (!message) {
      result.textContent = 'Please say what happened.';
      result.className = 'rp-result rp-bad';
      return;
    }

    send.disabled = true;
    result.textContent = 'Sending…';
    result.className = 'rp-result';

    try {
      const res = await fetch(`${BASE}/api/v1/contact`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          kind,
          message,
          subject: String(data.get('subject') || ''),
          from: String(data.get('from') || ''),
          honeypot: String(data.get('honeypot') || ''),
          // Only for bugs. A note saying hello does not need somebody's screen
          // size, and collecting it anyway is the kind of thing that makes a
          // privacy line stop being true.
          context: kind === 'bug' ? reportContext(map) : undefined,
        }),
      });

      const body = await res.json().catch(() => ({})) as { message?: string; error?: string };
      if (res.ok) {
        result.textContent = body.message || 'Thank you — that has been passed on.';
        result.className = 'rp-result rp-good';
        form.reset();
        // Long enough to read the answer, short enough not to sit there.
        window.setTimeout(() => dialog.close(), 2_200);
      } else {
        result.textContent = body.error || 'That did not go through. Please try again shortly.';
        result.className = 'rp-result rp-bad';
      }
    } catch {
      result.textContent = 'Could not reach the server. Please try again shortly.';
      result.className = 'rp-result rp-bad';
    } finally {
      send.disabled = false;
    }
  });
}
